/**
 * Screen views from the router (IMPLEMENTATION.md R9 b): every resolved navigation is a
 * `screen.viewed` for the route pattern, and leaving a screen is a `screen.left` carrying how long
 * it was open, so the rollup credits minutes to the screen they were spent on. The `$tenant`
 * parameter of the route names the company the batch is filed under.
 */
import type { AnyRouter } from '@tanstack/react-router';
import { retract, setAnalyticsTenant, track } from './events';

interface Open {
  screen: string;
  since: number;
}

/** The pattern of the deepest match, or `/` when nothing matched. */
export function screenOf(router: AnyRouter): { screen: string; tenant: string | null } {
  const matches = router.state.matches;
  const last = matches[matches.length - 1];
  const params = (last?.params ?? {}) as { tenant?: string };
  return { screen: String(last?.routeId ?? '/'), tenant: params.tenant ?? null };
}

let open: Open | null = null;

/** Under this, a screen was passed through, not looked at. */
const HOP_MS = 400;

/** The screen open right now, for events that happen on it (a refused request, an error). */
export function currentScreen(): string | undefined {
  return open?.screen;
}

export function watchScreens(router: AnyRouter, now: () => number = Date.now): () => void {
  open = null;
  const leave = () => {
    if (!open) return;
    const durationMs = now() - open.since;
    // A screen left inside a moment was a redirect hop (the index route on its way to the
    // dashboard), not a visit: it would only clutter the paths (R10 QA, 2026-09-21).
    if (durationMs >= HOP_MS) track('screen.left', { screen: open.screen, durationMs });
    open = null;
  };
  const arrive = () => {
    const { screen, tenant } = screenOf(router);
    setAnalyticsTenant(tenant);
    if (open?.screen === screen) return;
    const leaving = open;
    if (leaving !== null && now() - leaving.since < HOP_MS) retract(leaving.screen);
    leave();
    track('screen.viewed', { screen });
    open = { screen, since: now() };
  };
  const unsubscribe = router.subscribe('onResolved', arrive);
  window.addEventListener('pagehide', leave);
  arrive();
  return () => {
    unsubscribe();
    window.removeEventListener('pagehide', leave);
  };
}
